export type CaptureMode = 'voice_creation' | 'listener_personalization';

export type CaptureStep =
  | 'intro'
  | 'permission'
  | 'sound_check'
  | 'takes'
  | 'bookends'
  | 'review'
  | 'training'
  | 'done'
  | 'error';

export interface CaptureState {
  mode: CaptureMode;
  step: CaptureStep;
  takeIndex: number;
  completedTakeIndexes: number[];
  completedClipKeys: Array<'intro' | 'name' | 'goodnight'>;
  pendingUpload: boolean;
  error: string | null;
}

export type CaptureEvent =
  | { type: 'BEGIN_PERMISSION' }
  | { type: 'PERMISSION_GRANTED' }
  | { type: 'PERMISSION_DENIED'; message?: string }
  | { type: 'SOUND_CHECK_PASSED'; requiredClipKeys?: Array<'intro' | 'name' | 'goodnight'> }
  | { type: 'SOUND_CHECK_RETRY' }
  | { type: 'RESUME_PROGRESS'; takeIndexes: number[]; clipKeys: Array<'intro' | 'name' | 'goodnight'> }
  | { type: 'TAKE_UPLOAD_STARTED' }
  | { type: 'TAKE_UPLOAD_FAILED' }
  | { type: 'TAKE_UPLOAD_ACCEPTED'; index: number }
  | { type: 'BOOKEND_UPLOAD_FAILED' }
  | { type: 'BOOKEND_UPLOAD_ACCEPTED'; key: 'intro' | 'name' | 'goodnight' }
  | { type: 'NEXT_TAKE' }
  | { type: 'OPEN_REVIEW' }
  | { type: 'START_TRAINING' }
  | { type: 'COMPLETE' }
  | { type: 'RETRY' }
  | { type: 'FAIL'; message: string };

export function initialCaptureState(mode: CaptureMode): CaptureState {
  return { mode, step: 'intro', takeIndex: 0, completedTakeIndexes: [], completedClipKeys: [], pendingUpload: false, error: null };
}

export function captureReducer(state: CaptureState, event: CaptureEvent): CaptureState {
  switch (event.type) {
    case 'BEGIN_PERMISSION': return { ...state, step: 'permission', error: null };
    case 'PERMISSION_GRANTED': return { ...state, step: 'sound_check', error: null };
    // Keep a denied permission in the permission step so the next action is
    // still an accurate browser-permission retry, not a generic fatal error.
    case 'PERMISSION_DENIED': return { ...state, step: 'permission', error: event.message || 'Luma can’t access your microphone. Allow microphone access in your browser, then try again.' };
    case 'RESUME_PROGRESS': return {
      ...state,
      completedTakeIndexes: Array.from(new Set(event.takeIndexes.filter((index) => index >= 0 && index < 6))),
      completedClipKeys: Array.from(new Set(event.clipKeys)),
    };
    case 'SOUND_CHECK_PASSED': {
      const nextTake = [0, 1, 2, 3, 4, 5].find((index) => !state.completedTakeIndexes.includes(index));
      const allListenerClips = (event.requiredClipKeys || ['intro', 'name', 'goodnight'] as const).every((key) => state.completedClipKeys.includes(key));
      if (state.mode === 'listener_personalization') return { ...state, step: allListenerClips ? 'review' : 'bookends', error: null };
      return { ...state, step: nextTake === undefined ? 'review' : 'takes', takeIndex: nextTake ?? state.takeIndex, error: null };
    }
    case 'SOUND_CHECK_RETRY': return { ...state, step: 'sound_check', error: null };
    case 'TAKE_UPLOAD_STARTED': return { ...state, pendingUpload: true, error: null };
    case 'TAKE_UPLOAD_FAILED': return { ...state, pendingUpload: false, error: null };
    case 'TAKE_UPLOAD_ACCEPTED': return { ...state, pendingUpload: false, completedTakeIndexes: Array.from(new Set([...state.completedTakeIndexes, event.index])), error: null };
    case 'BOOKEND_UPLOAD_FAILED': return { ...state, pendingUpload: false, error: null };
    case 'BOOKEND_UPLOAD_ACCEPTED': return { ...state, pendingUpload: false, completedClipKeys: Array.from(new Set([...state.completedClipKeys, event.key])), error: null };
    case 'NEXT_TAKE': {
      const nextTake = [0, 1, 2, 3, 4, 5].find((index) => !state.completedTakeIndexes.includes(index));
      return nextTake === undefined
        ? { ...state, step: 'review', pendingUpload: false }
        : { ...state, takeIndex: nextTake, pendingUpload: false };
    }
    case 'OPEN_REVIEW': return { ...state, step: 'review', pendingUpload: false, error: null };
    case 'START_TRAINING': return { ...state, step: 'training', pendingUpload: false, error: null };
    case 'COMPLETE': return { ...state, step: 'done', pendingUpload: false, error: null };
    case 'RETRY': return { ...state, step: state.mode === 'voice_creation' ? 'takes' : 'bookends', pendingUpload: false, error: null };
    case 'FAIL': return { ...state, step: 'error', pendingUpload: false, error: event.message };
    default: return state;
  }
}
